<template>
  <b-modal
    :id="modalId"
    :title="product ? product.product : 'Product Intelligence'"
    size="xl"
    hide-footer
    scrollable
    @show="load"
    @hidden="reset"
  >
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">Loading intelligence data...</p>
    </div>

    <div v-else-if="!snapshot && !loadError" class="text-center py-5 text-muted">
      No intelligence data available for this product.
    </div>

    <div v-else-if="loadError" class="text-center py-5">
      <p class="text-danger">{{ loadError }}</p>
      <button class="btn btn-sm btn-outline-secondary" @click="load">Retry</button>
    </div>

    <div v-else>
      <!-- Tab nav -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item" v-for="tab in tabs" :key="tab.key">
          <a class="nav-link" :class="{ active: activeTab === tab.key }" href="#" @click.prevent="activeTab = tab.key">
            {{ tab.label }}
            <span v-if="tab.key === 'concerns' && activeConcernCount" class="badge badge-danger ml-1">{{ activeConcernCount }}</span>
          </a>
        </li>
      </ul>

      <!-- Overview tab -->
      <div v-if="activeTab === 'overview'">
        <div class="row text-center mb-3">
          <div class="col-6 col-md-3 border-right">
            <div class="h4 font-weight-bold">{{ snapshot.remaining_units != null ? snapshot.remaining_units : '—' }}</div>
            <small class="text-muted">In Stock</small>
          </div>
          <div class="col-6 col-md-3 border-right">
            <div class="h4 font-weight-bold" :class="daysOfStockClass">
              {{ snapshot.days_of_stock_7d != null ? Math.round(snapshot.days_of_stock_7d) + 'd' : '—' }}
            </div>
            <small class="text-muted">Days of Stock (7d rate)</small>
          </div>
          <div class="col-6 col-md-3 border-right">
            <div class="h4 font-weight-bold">{{ snapshot.sold_30d != null ? snapshot.sold_30d : '—' }}</div>
            <small class="text-muted">Sold (30d)</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="h4 font-weight-bold">
              <span :class="trendClass(snapshot.velocity_trend)">
                <i :class="trendIcon(snapshot.velocity_trend)"></i>
                {{ snapshot.velocity_trend || '—' }}
              </span>
            </div>
            <small class="text-muted">Velocity Trend</small>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <div class="card card-body bg-light border-0">
              <div class="d-flex justify-content-between mb-2">
                <small class="text-muted font-weight-bold">VELOCITY (avg daily)</small>
              </div>
              <div class="d-flex justify-content-between">
                <div class="text-center">
                  <div class="font-weight-bold">{{ fmt(snapshot.avg_daily_7d) }}</div>
                  <small class="text-muted">7d avg</small>
                </div>
                <div class="text-center">
                  <div class="font-weight-bold">{{ fmt(snapshot.avg_daily_30d) }}</div>
                  <small class="text-muted">30d avg</small>
                </div>
                <div class="text-center">
                  <div class="font-weight-bold">{{ fmt(snapshot.avg_daily_90d) }}</div>
                  <small class="text-muted">90d avg</small>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card card-body bg-light border-0">
              <div class="d-flex justify-content-between mb-2">
                <small class="text-muted font-weight-bold">COST OF GOODS SOLD</small>
              </div>
              <div class="d-flex justify-content-between">
                <div class="text-center">
                  <div class="font-weight-bold">{{ fmtCurrency(snapshot.cogs_30d) }}</div>
                  <small class="text-muted">30d COGS</small>
                </div>
                <div class="text-center">
                  <div class="font-weight-bold">{{ fmtCurrency(snapshot.cogs_90d) }}</div>
                  <small class="text-muted">90d COGS</small>
                </div>
                <div class="text-center">
                  <div class="font-weight-bold">{{ fmtCurrency(snapshot.cost_of_stock) }}</div>
                  <small class="text-muted">Stock cost</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="snapshot.days_since_last_sale != null" class="alert alert-light border mb-3">
          Last sale was <strong>{{ snapshot.days_since_last_sale }} days ago</strong>.
          <span v-if="snapshot.days_since_last_sale > 30" class="text-warning ml-1">
            <i class="bi bi-exclamation-triangle"></i> Long inactivity
          </span>
        </div>

        <div v-if="snapshot.substitution_rate > 0" class="alert alert-info border mb-3">
          <i class="bi bi-arrow-left-right"></i>
          <strong>{{ (snapshot.substitution_rate * 100).toFixed(1) }}%</strong> of 30d sales used a substitute item.
        </div>

        <div class="text-muted">
          Computed at {{ snapshot.computed_at ? new Date(snapshot.computed_at).toLocaleString() : '—' }}
        </div>
      </div>

      <!-- Concerns tab -->
      <div v-if="activeTab === 'concerns'">
        <div v-if="concerns.length === 0" class="text-center py-4 text-muted">
          No active concerns for this product.
        </div>
        <concern-card
          v-for="c in concerns"
          :key="c.id"
          :concern="c"
          :acting="actingConcern === c.id"
          @view="openConcernDetail(c)"
          @snooze="quickAction(c, 'snooze')"
          @resolve="quickAction(c, 'resolve')"
        />

        <concern-detail-modal
          modal-id="pip-concern-detail"
          :concern="selectedConcern"
          @updated="onConcernUpdated"
          @hidden="selectedConcern = null"
        />
      </div>

      <!-- Velocity tab -->
      <div v-if="activeTab === 'velocity'">
        <div v-if="velocityLoading" class="text-center py-4">
          <b-spinner /> Loading velocity history...
        </div>
        <div v-else-if="velocityData.length === 0" class="text-center py-4 text-muted">
          No daily velocity data available.
        </div>
        <div v-else>
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Date</th>
                <th class="text-right">Units Sold</th>
                <th class="text-right">Orders</th>
                <th class="text-right">Avg Daily (7d)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in velocityData" :key="row.date">
                <td>{{ row.date }}</td>
                <td class="text-right">{{ row.units_sold || 0 }}</td>
                <td class="text-right">{{ row.order_count || 0 }}</td>
                <td class="text-right">{{ fmt(row.avg_7d) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ConcernCard from './ConcernCard.vue';
import ConcernDetailModal from './ConcernDetailModal.vue';
import SeverityBadge from './SeverityBadge.vue';

export default {
  name: 'ProductIntelligencePanel',
  components: { ConcernCard, ConcernDetailModal, SeverityBadge },
  props: {
    product:  { type: Object, default: null },
    modalId:  { type: String, default: 'product-intelligence-modal' },
  },
  data() {
    return {
      loading:        false,
      loadError:      null,
      snapshot:       null,
      concerns:       [],
      velocityData:   [],
      velocityLoading:false,
      activeTab:      'overview',
      actingConcern:  null,
      selectedConcern:null,
      tabs: [
        { key: 'overview',  label: 'Overview' },
        { key: 'concerns',  label: 'Concerns' },
        { key: 'velocity',  label: 'Velocity' },
      ],
    };
  },
  computed: {
    activeConcernCount() {
      return this.concerns.filter(c => c.status === 'ACTIVE').length;
    },
    daysOfStockClass() {
      const d = this.snapshot?.days_of_stock_7d;
      if (d == null) return '';
      if (d <= 3)  return 'text-danger';
      if (d <= 7)  return 'text-warning';
      return 'text-success';
    },
  },
  methods: {
    async load() {
      if (!this.product) return;
      this.loading   = true;
      this.loadError = null;

      try {
        const [snapRes, concRes] = await Promise.all([
          axios.get(`/api/intelligence/snapshots/${this.product.id}`),
          axios.get('/api/intelligence/concerns', { params: { product_id: this.product.id, status: 'ACTIVE' } }),
        ]);
        this.snapshot = snapRes.data.snapshot || snapRes.data;
        this.concerns = concRes.data.data || concRes.data.concerns || [];
      } catch (e) {
        this.loadError = 'Failed to load intelligence data.';
      } finally {
        this.loading = false;
      }

      this.loadVelocity();
    },
    async loadVelocity() {
      if (!this.product) return;
      this.velocityLoading = true;
      try {
        const res = await axios.get(`/api/intelligence/velocity/${this.product.id}`);
        this.velocityData = res.data.data || res.data.velocity || [];
      } catch (_) {
        this.velocityData = [];
      } finally {
        this.velocityLoading = false;
      }
    },
    reset() {
      this.snapshot        = null;
      this.concerns        = [];
      this.velocityData    = [];
      this.activeTab       = 'overview';
      this.loadError       = null;
      this.selectedConcern = null;
    },
    async quickAction(concern, action) {
      this.actingConcern = concern.id;
      try {
        await axios.patch(`/api/intelligence/concerns/${concern.id}/${action}`);
        this.$toasts.push({ type: 'success', message: `Concern ${action}d.` });
        this.concerns = this.concerns.filter(c => c.id !== concern.id);
      } catch (e) {
        this.$toasts.push({ type: 'error', message: `Failed to ${action} concern.` });
      } finally {
        this.actingConcern = null;
      }
    },
    openConcernDetail(concern) {
      this.selectedConcern = concern;
      this.$bvModal.show('pip-concern-detail');
    },
    onConcernUpdated({ id }) {
      this.concerns = this.concerns.filter(c => c.id !== id);
      this.selectedConcern = null;
    },
    trendClass(trend) {
      return { UP: 'text-success', DOWN: 'text-danger', FLAT: 'text-muted', UNKNOWN: 'text-muted' }[trend] || 'text-muted';
    },
    trendIcon(trend) {
      return { UP: 'bi bi-arrow-up-right', DOWN: 'bi bi-arrow-down-right', FLAT: 'bi bi-arrow-right', UNKNOWN: 'bi bi-dash' }[trend] || 'bi bi-dash';
    },
    fmt(val) {
      if (val == null) return '—';
      return Number(val).toFixed(2);
    },
    fmtCurrency(val) {
      if (val == null) return '—';
      return this.$root.convertCurrencyToLocal ? this.$root.convertCurrencyToLocal(val) : Number(val).toLocaleString();
    },
  },
};
</script>
